import { useNewsCategoryLabels } from '@/context/newsCategoryLabelsContext';
import { getNewsCategoryLabel } from '@/util/storyblok/news-categories';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';
import styles from './FeaturedNewsItem.module.scss';
import { setupTransitionInTimeline } from './FeaturedNewsItem.transitions';
import type { FeaturedNewsItemProps, FeaturedNewsItemRef } from './FeaturedNewsItem.data';
import { useScrollTransition } from 'util/react-transition-component';
import { cleanMediaQueries } from '../../../../hooks/useDeviceStateTracker';
import Image from '../../../atoms/image/Image';
import { protocolRelativeToAbsoluteUrl, stripLocaleFromUrl } from '../../../../util/url.utils';
import useIsomorphicLayoutEffect from '../../../../hooks/useIsomorphicLayoutEffect';
import { formatDate } from '../../../../util/dates.utils';
import Text from '../../../atoms/text/Text';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import Heading from '../../../atoms/heading/Heading';
import { renderCta } from '../../../../util/cta/cta.template';
import type { CtaRefType } from '../../../../util/cta/cta.data';
import { useMobileViewport } from '../../../../hooks/useMobileViewport';
import sharedVariables from '../../../../data/shared-variable/shared-variables.json';
import ScrollTransition from '../../../unlisted/scroll-transition/ScrollTransition';
import { isTouch } from '../../../../hooks/useIsDesktopDevice';
import PrimaryLink from '../../../atoms/primary-link/PrimaryLink';
import type { PrimaryLinkRef } from '../../../atoms/primary-link/PrimaryLink.data';
// import { scaleImage } from '../../news-overview/news-overview-item/NewsOverviewItem.transitions';
import useRouterPush from '../../../../hooks/useRouterPush';
import { useRouter } from 'next/router';
import {
  addImageInVideoObject,
  getFallbackVideo,
  hasVideo,
} from '../../../../util/storyblok/data.utils';
import Video from '../../../atoms/video/Video';
import type { ImageRef } from '../../../atoms/image/Image.data';
import type { VideoRef } from '../../../atoms/video/Video.data';
import { useGlobalStore } from '../../../../stores/global';
import { useCanPlayWebmContext } from '../../../../context/canPlayWebmContext';

export default function FeaturedNewsItem({
  blok,
  className,
  alignRight,
  canTransitionIn,
  ref,
}: FeaturedNewsItemProps & { ref?: Ref<FeaturedNewsItemRef> }): ReactElement {
  const { locale } = useRouter();
  const categoryLabels = useNewsCategoryLabels();
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<CtaRefType | null>;
  const { content } = blok.news;
  const imageHolder = useRef<HTMLDivElement>(null);
  const [formattedDate, setFormattedDate] = useState<string | undefined | null>(
    formatDate(content?.date || content?.publish_date, 'D MMM YYYY', locale),
  );
  const titleRef = useRef<HeadingRef>(null);
  const categoryRef = useRef<HeadingRef>(null);
  const dateRef = useRef<HeadingRef>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isMobileViewport = useMobileViewport(sharedVariables.deviceState.MIN_768, false);
  const imageRef = useRef<ImageRef | VideoRef>(null);
  const gifRef = useRef<HTMLVideoElement>(null);
  const downloadLinkRef = useRef<PrimaryLinkRef>(null);
  const routerPush = useRouterPush();
  const globalStore = useGlobalStore();
  const isDesktopViewport = !useMobileViewport(sharedVariables.deviceState.MIN_1024, true);
  const [isHover, setIsHover] = useState(false);
  const { canPlayWebm } = useCanPlayWebmContext();

  const { inView } = useScrollTransition({
    scroll: {
      canTransitionIn,
      trigger: elementRef,
      vars: {},
      enableInView: true,
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        titleRef,
        categoryRef,
        dateRef,
        downloadLinkRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const { key_image, key_video, thumbnail_video } = content;

  let src;
  let srcDesktop;
  if (key_image && key_image?.length > 0) {
    src = protocolRelativeToAbsoluteUrl(key_image[0].image_portrait);
    srcDesktop = protocolRelativeToAbsoluteUrl(key_image[0].image_landscape);

    if (!src) {
      src = protocolRelativeToAbsoluteUrl(key_image[0].image_landscape);
    }
  }

  useIsomorphicLayoutEffect(() => {
    setFormattedDate(formatDate(content?.date || content?.publish_date, 'D MMM YYYY', locale));
  }, [content.date, content.publish_date, locale]);

  const onMouseEnter = useCallback(() => {
    setIsHover(true);
    // scaleImage(imageRef, true, 1.05);
  }, []);

  const onMouseLeave = useCallback(() => {
    setIsHover(false);
    // scaleImage(imageRef, false);
  }, []);

  const onDownloadClick = useCallback(() => {
    const url = stripLocaleFromUrl(blok.news.full_slug, locale);
    routerPush(`${url}#download-pdf`);
  }, [blok.news.full_slug, locale, routerPush]);

  let media = <></>;

  if ((key_video && hasVideo(key_video)) || (thumbnail_video && hasVideo(thumbnail_video))) {
    media = (
      <Video
        ref={imageRef as RefObject<VideoRef | null>}
        blok={addImageInVideoObject(
          getFallbackVideo(thumbnail_video, key_video),
          srcDesktop || src,
        )}
        objectFit="cover"
        controls={false}
        playButton={false}
        muted
        loop
        openInModal={false}
        playing={inView}
        innerTransition={{
          type: 'parallax',
          config: {
            speed: -2,
          },
        }}
        className={classNames(styles.video, 'abs-fill')}
      />
    );
  } else if (key_image?.[0]?.animated_image?.filename && canPlayWebm) {
    media = (
      <video
        ref={gifRef}
        src={key_image?.[0]?.animated_image.filename}
        autoPlay
        muted
        controls={false}
        playsInline
        loop
        className={classNames(styles.animatedImage)}
      />
    );
  } else if (key_image?.[0] && src && !hasVideo(key_video) && !hasVideo(thumbnail_video)) {
    media = (
      <Image
        ref={imageRef as RefObject<ImageRef | null>}
        src={src}
        srcDesktop={srcDesktop || src}
        layout="fill"
        className={classNames(styles.image, 'abs-fill')}
        sizes={`${cleanMediaQueries.MIN_768} 1200px, ${cleanMediaQueries.MIN_1920} 1680px, 100vw`}
        alt={key_image[0].alt_text}
        innerTransition={{
          type: 'parallax',
          config: {
            speed: -2,
          },
        }}
      />
    );
  }

  return renderCta({
    elementRef,
    blok,
    props: {
      link: {
        href: blok.news.full_slug,
        uuid: blok._uid,
      },
      className: classNames(styles.featuredNewsItem, className, {
        [styles.alignRight]: alignRight,
      }),
      onMouseEnter,
      onMouseLeave,
    },
    innerTemplate: (
      <>
        {((key_image?.[0] && src) ||
          (key_video && hasVideo(key_video)) ||
          (thumbnail_video && hasVideo(thumbnail_video))) && (
          <div ref={imageHolder} className={styles.imageHolder}>
            <ScrollTransition
              as="div"
              disableTriggerInOnScroll={isMobileViewport}
              className={classNames(styles.imageContainer, 'abs-fill')}
              type="parallax"
              config={{
                speed: 1.5,
                ease: 'Power2.easeOut',
              }}
              vars={{
                start: 'top bottom',
                end: 'center top',
              }}
            >
              {media}
            </ScrollTransition>
          </div>
        )}

        <div ref={contentRef} className={classNames(styles.content)}>
          <div className={classNames(styles.innerContent)}>
            <Heading
              ref={categoryRef}
              transitionInType={isTouch ? 'none' : 'splitWordsSlideInUp'}
              transitionOutType={isTouch ? 'none' : 'splitWordsSlideOutUp'}
              className={classNames(styles.category)}
              as="h3"
              size="label-small"
              disableTriggerInOnScroll={!isTouch}
              canTransitionIn={canTransitionIn}
            >
              {getNewsCategoryLabel(categoryLabels, content.category)}
            </Heading>

            <Heading
              ref={titleRef}
              transitionInType={isTouch ? 'none' : 'splitWordsSlideInUp'}
              transitionOutType={isTouch ? 'none' : 'splitWordsSlideOutUp'}
              className={classNames(styles.title)}
              as="h2"
              size="quote"
              disableTriggerInOnScroll={!isTouch}
              canTransitionIn={canTransitionIn}
            >
              {content.title}
            </Heading>

            {!!content.pardot_id && (
              <PrimaryLink
                ref={downloadLinkRef}
                textVariant="body-standard"
                className={classNames(styles.downloadLink)}
                label={
                  content.pardot_download_label ||
                  globalStore.globalSettings.form_pardot_pdf_download_label
                }
                forceTag="div"
                disableTriggerInOnScroll={!isTouch}
                transitionInType={isTouch ? 'none' : 'splitWordsSlideInUp'}
                icon="download"
                onClick={onDownloadClick}
              />
            )}

            <Text
              ref={dateRef}
              transitionInType={isTouch ? 'none' : 'splitWordsSlideInUp'}
              transitionOutType={isTouch ? 'none' : 'splitWordsSlideOutUp'}
              className={classNames(styles.date)}
              as="p"
              size="body-standard"
              disableTriggerInOnScroll={!isTouch}
              canTransitionIn={canTransitionIn}
            >
              {formattedDate}
            </Text>

            {isDesktopViewport && (
              <PrimaryLink
                textVariant="body-standard"
                className={classNames(styles.cta)}
                label={globalStore.globalSettings.view_article}
                forceTag="div"
                disableTriggerInOnScroll
                isVisible={{
                  onMount: false,
                  value: isHover,
                }}
                transitionInType="splitWordsSlideInUp"
                transitionOutType="fadeOut"
              />
            )}
          </div>
        </div>
      </>
    ),
  });
}
